import { useEffect, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// Restyled to the mock; the behaviour below is unchanged, including the closed-signup branch that
// org installs depend on.
export default function RegisterForm() {
  const { t, i18n } = useTranslation();
  const { signUpWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // null = checking, true = open, false = closed (org/air-gapped installs).
  const [signupOpen, setSignupOpen] = useState(null);

  useEffect(() => {
    let mounted = true;
    supabase.rpc('is_public_signup_open').then(({ data, error: err }) => {
      if (!mounted) return;
      // Fail open only if the RPC is missing; an explicit false closes registration.
      setSignupOpen(err ? true : data !== false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setSubmitting(true);
    const { data, error: err } = await signUpWithEmail(email, password, i18n.language);
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    if (data.session) {
      navigate('/');
    } else {
      // Email confirmation may be enabled in the Supabase dashboard. This is the moment the
      // message is true, which is why it is not drawn as a permanent hint the way the mock has it.
      setInfo(t('register.confirmEmail'));
    }
  };

  const onGoogle = async () => {
    setError('');
    const { error: err } = await signInWithGoogle();
    if (err) setError(err.message);
  };

  if (signupOpen === false) {
    return (
      <div className="space-y-2 text-center" data-testid="register-closed">
        <p className="text-base font-medium"><T k="register.closedTitle" /></p>
        <p className="text-sm text-muted-foreground"><T k="register.closed" /></p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3.5" data-testid="register-form">
      <div>
        <label htmlFor="register-email" className="mb-1 block text-xs font-medium text-muted-foreground">
          {t('register.email')}
        </label>
        <Input
          id="register-email"
          type="email"
          required
          placeholder={t('login.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="register-email-input"
        />
      </div>
      <div>
        <label htmlFor="register-password" className="mb-1 block text-xs font-medium text-muted-foreground">
          {t('register.password')}
        </label>
        <Input
          id="register-password"
          type="password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="register-password-input"
        />
      </div>

      {/* See LoginForm: dark --destructive is too dark to read as text here. */}
      {error && (
        <p className="text-sm font-medium text-destructive dark:text-destructive" data-testid="register-error">
          {error}
        </p>
      )}
      {info && (
        <p className="text-sm font-medium text-success dark:text-success" data-testid="register-info">
          {info}
        </p>
      )}

      <Button
        type="submit"
        disabled={submitting || signupOpen === null}
        className="w-full"
        data-testid="register-submit-button"
      >
        {submitting ? t('register.submitting') : t('register.submit')}
      </Button>

      <div className="flex items-center gap-3 py-1.5">
        <span className="h-px flex-1 bg-border" />
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground"><T k="common.or" /></span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <Button type="button" variant="outline" onClick={onGoogle} className="w-full" data-testid="register-google-button">
        {t('register.google')}
      </Button>
    </form>
  );
}